import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

const SurveyDataTable = ({ communes }) => {
    const [surveys, setSurveys] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        commune: 'all',
        section: '',
        startDate: '',
        endDate: ''
    });
    const [sections, setSections] = useState([]);
    const [exporting, setExporting] = useState(false);

    // Fetch sections when commune changes (optional, if you have a sections table)
    useEffect(() => {
        if (filters.commune && filters.commune !== 'all') {
            // You can fetch sections from backend by commune
            api.get(`/sections/by-commune?commune=${filters.commune}`)
                .then(res => setSections(res.data.data || []))
                .catch(() => setSections([]));
        } else {
            setSections([]);
        }
    }, [filters.commune]);

    const fetchSurveys = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.commune && filters.commune !== 'all') params.append('commune', filters.commune);
            if (filters.section) params.append('section', filters.section);
            if (filters.startDate) params.append('startDate', filters.startDate);
            if (filters.endDate) params.append('endDate', filters.endDate);
            params.append('limit', 1000); // fetch many for display, but could paginate later
            const res = await api.get(`/surveys?${params}`);
            setSurveys(res.data.data || []);
        } catch (err) {
            console.error('Failed to fetch surveys:', err);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchSurveys();
    }, [fetchSurveys]);

    const handleFilterChange = (e) => {
        setFilters({ ...filters, [e.target.name]: e.target.value });
    };

    const exportToCSV = async () => {
        setExporting(true);
        try {
            const params = new URLSearchParams();
            if (filters.commune && filters.commune !== 'all') params.append('commune', filters.commune);
            if (filters.section) params.append('section', filters.section);
            if (filters.startDate) params.append('startDate', filters.startDate);
            if (filters.endDate) params.append('endDate', filters.endDate);
            params.append('limit', 10000); // large limit
            const res = await api.get(`/surveys?${params}`);
            const data = res.data.data;

            // Prepare CSV headers
            const headers = [
                'ID', 'Surveyor', 'Respondent', 'Date', 'Commune', 'Section', 'Synced',
                'Age', 'Gender', 'Accessible Location', 'Electricity', 'Payment Method', 'Biggest Problem', 'Recommended Location'
            ];
            const rows = data.map(row => [
                row.id, row.surveyor_name, row.respondent_name, row.survey_date,
                row.commune, row.section, row.synced ? 'Yes' : 'No',
                row.age, row.gender, row.accessible_location, row.electricity,
                row.payment_method, row.biggest_problem, row.recommended_location
            ]);
            const csvContent = [headers, ...rows].map(row => row.map(cell => `"${String(cell || '').replace(/"/g, '""')}"`).join(',')).join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.href = url;
            link.setAttribute('download', `surveys_${new Date().toISOString().slice(0,19)}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Export failed:', err);
            alert('Export failed');
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="survey-data-table">
            <div className="filters-bar">
                <select name="commune" value={filters.commune} onChange={handleFilterChange}>
                    <option value="all">All Communes</option>
                    {communes.map(c => <option key={c.commune} value={c.commune}>{c.commune}</option>)}
                </select>
                <input type="text" name="section" placeholder="Section" value={filters.section} onChange={handleFilterChange} />
                <input type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} placeholder="Start Date" />
                <input type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} placeholder="End Date" />
                <button onClick={fetchSurveys}>Apply Filters</button>
                <button onClick={exportToCSV} disabled={exporting}>{exporting ? 'Exporting...' : 'Export CSV'}</button>
            </div>
            {loading && <div className="loading">Loading surveys...</div>}
            {!loading && (
                <div className="table-wrapper">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>ID</th><th>Surveyor</th><th>Respondent</th><th>Date</th><th>Commune</th><th>Section</th><th>Synced</th>
                                <th>Age</th><th>Gender</th><th>Accessible Location</th><th>Electricity</th><th>Payment Method</th><th>Biggest Problem</th><th>Recommended Location</th>
                            </tr>
                        </thead>
                        <tbody>
                            {surveys.map(s => (
                                <tr key={s.id}>
                                    <td>{s.id}</td>
                                    <td>{s.surveyor_name}</td>
                                    <td>{s.respondent_name}</td>
                                    <td>{s.survey_date}</td>
                                    <td>{s.commune}</td>
                                    <td>{s.section}</td>
                                    <td>{s.synced ? '✓' : '✗'}</td>
                                    <td>{s.age}</td>
                                    <td>{s.gender}</td>
                                    <td>{s.accessible_location}</td>
                                    <td>{s.electricity}</td>
                                    <td>{s.payment_method}</td>
                                    <td>{s.biggest_problem}</td>
                                    <td>{s.recommended_location}</td>
                                </tr>
                            ))}
                            {surveys.length === 0 && <tr><td colSpan="14">No surveys found.</td></tr>}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default SurveyDataTable;